import { all, create, equalText, round, sum, } from 'mathjs';
import { validate as isUuid } from 'uuid';
import { globalApplication } from "../application";
import { isProtocolLink, } from "../entities";
const math = create(all);
function functionWithArgs(fn, args) {
    const fnWithArgs = fn;
    fnWithArgs.args = args;
    return fnWithArgs;
}
const autocalculationFunctions = {
    default: functionWithArgs((...args) => {
        if (args[0] === undefined) {
            return args[1];
        }
        return args[0];
    }, 2),
    sum: functionWithArgs((...args) => {
        return sum(...args);
    }, 1),
    equipProp: functionWithArgs((...args) => {
        const equipmentItemId = args[0];
        const fieldName = args[1];
        if (!globalApplication || !equipmentItemId) {
            return undefined;
        }
        try {
            const equipmentItem = globalApplication.get('localCache').getEquipmentItem(equipmentItemId);
            return equipmentItem.itemData[fieldName];
        }
        catch {
            return undefined;
        }
    }, 2),
    round: functionWithArgs((...args) => {
        const value = args[0];
        const roundDecimal = args[1];
        return roundDecimal ? round(value, roundDecimal) : round(value);
    }, 2),
    equalText: functionWithArgs((...args) => {
        return equalText(args[0], args[1]);
    }, 2),
};
function isAutocalculationFunction(functionName) {
    return Object.keys(autocalculationFunctions).includes(functionName);
}
math.import(autocalculationFunctions, { override: true });
function isFunctionNode(node) {
    return node.type === 'FunctionNode';
}
function isSymbolNode(node) {
    return node.type === 'SymbolNode';
}
function isAccessorNode(node) {
    return node.type === 'AccessorNode';
}
function isStringConstantNode(node) {
    return node.type === 'ConstantNode' && typeof node.value === 'string';
}
function getFormulaVariablesAndFunctions(formula) {
    const nodes = math.parse(formula);
    const variables = [];
    const functions = [];
    nodes.traverse((node, _, parent) => {
        if (isFunctionNode(node)) {
            functions.push({ name: node.fn.name, args: node.args, call: node.toString() });
        }
        if (isSymbolNode(node) &&
            (parent === null || (!(isFunctionNode(parent) && parent.fn === node) && !isAccessorNode(parent)))) {
            variables.push(node.name);
        }
        if (isAccessorNode(node) && isSymbolNode(node.object)) {
            variables.push(`${node.object.name}.${node.name}`);
        }
    });
    return { variables, functions };
}
function getGroupScope(group) {
    const scope = {};
    group.forEach((child) => {
        const childObject = child;
        const elementName = Object.keys(childObject);
        elementName.forEach((elementName) => {
            const value = childObject[elementName];
            if (value !== undefined && typeof value === 'number') {
                if (elementName in scope) {
                    scope[elementName].push(value);
                }
                else {
                    scope[elementName] = [value];
                }
            }
        });
    });
    return scope;
}
function getFormulaScope(formula, data) {
    const scope = Object.keys(data).reduce((prev, key) => {
        const value = data[key];
        if (typeof value === 'number') {
            prev[key] = value;
        }
        else if (typeof value === 'string' && isUuid(value)) {
            prev[key] = value;
        }
        else if (Array.isArray(value)) {
            prev[key] = getGroupScope(value);
        }
        else if (isProtocolLink(value) && typeof value.value === 'number') {
            prev[key] = value.value;
        }
        else if (value === undefined) {
            prev[key] = undefined;
        }
        return prev;
    }, {});
    const { variables: fields } = getFormulaVariablesAndFunctions(formula);
    fields.forEach((field) => {
        if (field in data === false) {
            scope[field] = undefined;
        }
    });
    return scope;
}
function isEquipPropFunctionCallValid(fn, availableVariables, localCache) {
    if (fn.name !== 'equipProp') {
        return;
    }
    const [equipmentItemNode, propNode] = fn.args;
    if (!isSymbolNode(equipmentItemNode)) {
        return {
            type: 'no_equipment_item_variable',
            variable: equipmentItemNode.toString(),
            call: fn.call,
        };
    }
    const equipmentItemVariable = availableVariables[equipmentItemNode.name];
    if (!equipmentItemVariable || equipmentItemVariable.type !== 'equipmentitem') {
        return {
            type: 'no_equipment_item_variable',
            variable: equipmentItemNode.name,
            call: fn.call,
        };
    }
    const equipmentList = typeof localCache === 'function'
        ? localCache('equipmentList', equipmentItemVariable.equipmentList)
        : localCache.getEquipmentList(equipmentItemVariable.equipmentList);
    const equipmentItemProps = getAvailableVariables(equipmentList?.itemSchema || [], localCache);
    if (!isStringConstantNode(propNode) || !equipmentItemProps.find(({ name }) => name === propNode.value)) {
        return {
            type: 'no_equipment_item_prop',
            prop: propNode.toString(),
            equipmentItemVariable: equipmentItemNode.name,
            call: fn.call,
        };
    }
}
function isSumFunctionCallValid(fn, availableVariables) {
    if (fn.name !== 'sum') {
        return;
    }
    const variableListNode = fn.args[0];
    if (!isAccessorNode(variableListNode) || !availableVariables[variableListNode.toString()]?.parentElement) {
        return {
            type: 'no_variable_list',
            variable: variableListNode.toString(),
            call: fn.call,
        };
    }
}
function isFormulaValid(formula, schema, localCache) {
    const availableVariables = getAvailableVariables(schema, localCache).reduce((dict, variable) => ({ ...dict, [variable.name]: variable }), {});
    try {
        const { variables, functions } = getFormulaVariablesAndFunctions(formula);
        const invalidVariables = [];
        variables.forEach((variable) => {
            if (!availableVariables[variable]) {
                invalidVariables.push(variable);
            }
        });
        const invalidFunctions = [];
        const wrongFunctionUsages = [];
        functions.forEach((fn) => {
            if (!isAutocalculationFunction(fn.name)) {
                invalidFunctions.push(fn.name);
            }
            else {
                const { args } = autocalculationFunctions[fn.name];
                if (args !== fn.args.length) {
                    wrongFunctionUsages.push({
                        type: 'args_count',
                        call: fn.call,
                        args: fn.args.length,
                        functionName: fn.name,
                        expectedArgs: args,
                    });
                }
                else if (fn.name === 'equipProp') {
                    const wrongUsage = isEquipPropFunctionCallValid(fn, availableVariables, localCache);
                    if (wrongUsage) {
                        wrongFunctionUsages.push(wrongUsage);
                    }
                }
                else if (fn.name === 'sum') {
                    const wrongUsage = isSumFunctionCallValid(fn, availableVariables);
                    if (wrongUsage) {
                        wrongFunctionUsages.push(wrongUsage);
                    }
                }
            }
        });
        if (invalidVariables.length > 0 || invalidFunctions.length > 0 || wrongFunctionUsages.length > 0) {
            return { invalidVariables, invalidFunctions, wrongFunctionUsages };
        }
    }
    catch (err) {
        return false;
    }
    return true;
}
function getAvailableVariables(schema, localCache) {
    const variables = schema.filter((element) => element.type === 'number' || element.type === 'autocalculation');
    const equipmentItemVariables = schema.reduce((allVariables, element) => {
        if (element.type === 'equipmentitem') {
            const equipmentItemProperties = [];
            const equipmentList = typeof localCache === 'function'
                ? localCache('equipmentList', element.equipmentList)
                : localCache.getEquipmentList(element.equipmentList);
            if (!equipmentList) {
                return allVariables;
            }
            equipmentItemProperties.push(...equipmentList.itemSchema.filter((element) => element.type === 'number' || element.type === 'autocalculation'));
            return [
                ...allVariables,
                {
                    ...element,
                    label: `${element.label}${equipmentItemProperties
                        .map(({ name, label }) => `\n\t\t"${name}" - ${label} (property of equipment item '${element.name}')`)
                        .join('')}`,
                },
            ];
        }
        else {
            return allVariables;
        }
    }, []);
    variables.push(...equipmentItemVariables);
    const hasGroupElement = schema.some((element) => element.type === 'group');
    if (hasGroupElement) {
        const groupVariables = schema.reduce((acc, element) => {
            if (element.type === 'group' && element.children && Array.isArray(element.children)) {
                element.children.forEach((subElement) => {
                    if (subElement.type === 'number' || subElement.type === 'autocalculation') {
                        acc.push({
                            ...subElement,
                            name: `${element.name}.${subElement.name}`,
                            label: `List of values '${subElement.label}' of all the '${element.repeatableLabel}' from '${element.label}'`,
                            parentElement: element,
                        });
                    }
                });
            }
            return acc;
        }, []);
        variables.push(...groupVariables);
    }
    return variables;
}
export const autocalculation = {
    execute(formula, data) {
        const scope = getFormulaScope(formula, data);
        const result = math.evaluate(formula.replace(/[\n\r]/gm, ' '), scope);
        if (typeof result === 'number' && !Number.isFinite(result)) {
            return undefined;
        }
        if (typeof result !== 'number' && typeof result !== 'string' && typeof result !== 'boolean') {
            return undefined;
        }
        return result;
    },
    getFormulaVariablesAndFunctions,
    isFormulaValid,
    getAvailableVariables,
};
//# sourceMappingURL=index.js.map